import type {
    BodyParameters,
    FeedbackMessage,
    QueryParameters,
    CallbackStringParam,
} from "../ui_general/GeneralInterfaces";
import {ApiEndPoints, FeedbackMessageTypes, HttpMethods, LabelTextMessageTypes} from "../Settings";
import { buildApiUrl, createErrorFeedbackMessage } from "./GeneralUtilities";
import { getAccessToken } from '../../auth/tokenStore';


/**
 * This class defines a fetch controller that is used to issue fetch commands to the backend. It mainly provides
 * the processFetchRequest routine which handles such requests. Moreover, this controller provides functionality to
 * cancel all pending fetch requests emitted under this controller, for instance, in case that a user navigates away
 * from a given view that issued fetch requests.
 */
export class FetchControl {
    /** Holds an instance of an AbortController which allows to abort all pending fetch requests. */
    private readonly abort_controller: AbortController;
    /** Holds a record of pending fetch requests identified by a running number together with the request time*/
    private readonly pending_fetch_requests: Record<number, Date>;
    /** Holds the number of fetch requests that have been emitted. */
    private running_fetch_requests_identifier: number;


    /**
     * Constructor. Initializes abort controller that can be used to abort all pending fetch requests. Also, the
     * constructor initializes the control variables of pending fetch requests.
     */
    constructor() {
        this.abort_controller = new AbortController();
        this.pending_fetch_requests = {};
        this.running_fetch_requests_identifier = 0;
    }


    /**
     * This routine cancels all pending fetch requests.
     */
    cancelAllPendingFetchRequests() {
        this.abort_controller.abort();
    }


    /**
     * Appends the record of pending fetch requests with a new entry using private variable
     * running_fetch_requests_identifier to construct a consecutive series of identifiers. The record also contains
     * the time of the emitted fetch request.
     * @returns The identifier of the new record entry.
     */
    registerPendingFetchRequest(): number {
        const fetch_request_nr = this.running_fetch_requests_identifier + 1;
        this.running_fetch_requests_identifier = this.running_fetch_requests_identifier + 1;
        this.pending_fetch_requests[fetch_request_nr] = new Date();
        return fetch_request_nr;
    }


    /**
     * Removes the entry with the given identifier from the record of pending fetch requests.
     * @param fetch_request_identifier Identifier of the pending fetch request.
     */
    removePendingFetchRequest(fetch_request_identifier: number) {
        if (fetch_request_identifier in this.pending_fetch_requests) {
            delete this.pending_fetch_requests[fetch_request_identifier];
        }
    }


    /**
     * Returns the number of pending fetch requests.
     */
    getNumberPendingFetchRequests(): number {
        return Object.entries(this.pending_fetch_requests).length;
    }


    /**
     * This routine is used whenever a fetch request needs to be invoked to communicate with the API-server.
     * It gathers the xsrf token and the relevant token nonce (if available) and includes these in every request in the
     * request header. The function allows to include query-string parameters, request body parameters and a single file
     * (optionally). The content-type of the request is application/json unless a file is specified. In that case, the
     * content-type is not explicitly set - resulting in multipart/form-data. The routine catches all errors and
     * throws a standardized FeedbackMessage in case of exception.
     *
     * @param api_endpoint End-point address of rest API, for instance: /api/login
     * @param query_parameters Optional query-string parameters to be included in request URL, for instance:
     * {model_id: 1}
     * rollback point is added to the query string. This is a convenience functionality. Alternatively, the rollback
     * point can also be provided directly in the query_parameters parameter. If not specified or undefined,
     * no rollback point is not added to the query string.
     * @param method HTTP request method to be used in restful API call, for instance: "GET"
     * @param body Optional parameters to be included in the request body of API call, for instance: {selected: true}.
     * UUIDs are automatically encoded as strings.
     * @param file Optional file to be uploaded along with the restful API call.
     * @returns Returns a Promise object that can be used to process the server response after it has been received.
     */
    requestFetchPromise(api_endpoint: ApiEndPoints,
                        method: HttpMethods,
                        query_parameters: QueryParameters,
                        body?: BodyParameters,
                        file?: File): Promise<any> {
        // Initialization request with desired http method
        const request_init: RequestInit = {method: method};

        // Set request headers (for file uploads, form data is sent instead of application/json)
        const request_headers: Record<string, string> = {'accept': 'application/json'}
        if (!file) request_headers['Content-Type'] = 'application/json';

        // Attach bearer token if available and not in debug mode.
        const token = getAccessToken() || 'No Token';
        request_headers['Authorization'] = `Bearer ${token}`;
        request_init.headers = new Headers(request_headers);

        // Set abort controller signal
        // TODO: Test and repair abort signal mechanism
        // request_init.signal = this.abort_controller.signal;

        // Construct api URL including query-parameters. If rollback_point_id is specified, it is incorporated.
        const url: string = buildApiUrl(
            api_endpoint,
            query_parameters
        );

        // Construct request body. If file is specified, form data is sent.
        if (file) {
            const formData = new FormData();
            if (body) {
                for (const [key, value] of Object.entries(body)) {
                    if (typeof value !== 'string')  {
                        throw createErrorFeedbackMessage(LabelTextMessageTypes.MALFORMED_FETCH_REQUEST);
                    }
                    formData.append(`${key}`, value);
                }
            }
            formData.append('file', file);
            request_init.body = formData;
        } else {
            if (body) request_init.body = JSON.stringify(body);
        }

        // Variable used to transfer the http status code from one promise to the following.
        let response_status_code: number | undefined = undefined;

        // Instantiating and returning the fetch request
        const fetch_request_identifier = this.registerPendingFetchRequest();
        return fetch(
            url,
            request_init
        ).then(response => {
            response_status_code = response.status;
            return response.json();
        }).then(response_body => {
            this.removePendingFetchRequest(fetch_request_identifier);
            if (!response_status_code || response_status_code < 200 || response_status_code >= 300) {
                const fetch_error: FeedbackMessage = {...response_body, message_type: FeedbackMessageTypes.ERROR };
                fetch_error.http_code = response_status_code;
                throw fetch_error;
            }

            return response_body;
        }).then(response_body => response_body,
                reason => {
                    this.removePendingFetchRequest(fetch_request_identifier);
                    // This is the error handling for any exception thrown in the fetch request. It is checks if reason
                    // is a feedback message. In this case, the feedback message is thrown. Otherwise, a generic network
                    // issue is assumed.
                    if ('message_type' in reason && 'message_text' in reason) throw reason;
                    if ('message_type' in reason && 'detail' in reason) {
                        reason = {...reason,
                            message_type: FeedbackMessageTypes.ERROR,
                            message_text: JSON.stringify(reason.detail)
                        }
                        throw reason;
                    }
                    throw createErrorFeedbackMessage(LabelTextMessageTypes.UNKNOWN_NETWORK_ERROR);
                })
    }


    //TODO: Double check this code
    /**
     * Requests a streaming response (text-based) from the backend and delivers chunks to a callback.
     * Useful for endpoints returning StreamingResponse, such as the chat API.
     *
     * @param api_endpoint Backend endpoint (e.g., ApiEndPoints.CHAT)
     * @param method HTTP method, typically POST
     * @param query_parameters Query-string parameters to include in the URL
     * @param onChunk Callback invoked with each decoded text chunk
     * @param body Optional JSON body to send along with the request
     * @returns Resolves when the stream completes; rejects with a FeedbackMessage on error
     */
    requestFetchStreamPromise(
        api_endpoint: ApiEndPoints,
        method: HttpMethods,
        query_parameters: QueryParameters,
        onChunk: CallbackStringParam,
        body?: BodyParameters,
    ): Promise<void> {
        const request_init: RequestInit = { method };

        // Set headers for text stream
        const request_headers: Record<string, string> = { 'accept': 'text/plain' };
        const token = getAccessToken() || 'No Token';
        request_headers['Authorization'] = `Bearer ${token}`;
        if (body) request_headers['Content-Type'] = 'application/json';
        request_init.headers = new Headers(request_headers);

        // Optional: wire abort signal when mechanism is repaired
        // request_init.signal = this.abort_controller.signal;

        const url: string = buildApiUrl(api_endpoint, query_parameters);
        const fetch_request_identifier = this.registerPendingFetchRequest();

        if (body) request_init.body = JSON.stringify(body);

        return fetch(url, request_init).then(async (response) => {
            try {
                if (!response.ok) {
                    const status = response.status;
                    // Try to read text body if present for context
                    const errText = await response.text().catch(() => undefined);
                    const feedback = createErrorFeedbackMessage(LabelTextMessageTypes.UNKNOWN_NETWORK_ERROR, status);
                    if (errText) feedback.technical_error_message = errText;
                    throw feedback;
                }
                if (!response.body) {
                    throw createErrorFeedbackMessage(LabelTextMessageTypes.UNKNOWN_NETWORK_ERROR);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    const chunk = decoder.decode(value, { stream: true });
                    if (chunk) onChunk(chunk);
                }
            } finally {
                this.removePendingFetchRequest(fetch_request_identifier);
            }
        }).catch((reason) => {
            this.removePendingFetchRequest(fetch_request_identifier);
            if ('message_type' in reason && 'message_text' in reason) throw reason as FeedbackMessage;
            throw createErrorFeedbackMessage(LabelTextMessageTypes.UNKNOWN_NETWORK_ERROR);
        });
    }
}
